import CFB from 'cfb';

/**
 * OLE2Frame Service - AutoCAD DXF OLE2FRAME desteği
 *
 * Yapı (gerçek AutoCAD analizi sonucu):
 * 1. Position Header (98 bytes): mysteryValue + 4x Vec3
 * 2. OLE Metadata Header (30 bytes): Boyut ve tip bilgileri
 * 3. CFB Container: OLE Compound File (D0CF11E0A1B11AE1)
 *    - Root Entry
 *    - Ole stream
 *    - CONTENTS stream (BMP data)
 *    - CompObj stream
 */

// Constants
const POSITION_HEADER_SIZE = 98;  // 2 + 24*4
const OLE_METADATA_SIZE = 30;
const TOTAL_HEADER_SIZE = POSITION_HEADER_SIZE + OLE_METADATA_SIZE;

/**
 * IEEE 754 double değerini little-endian buffer'a yazar
 */
function writeDoubleLE(buffer, offset, value) {
  const view = new DataView(buffer.buffer, buffer.byteOffset + offset, 8);
  view.setFloat64(0, value, true);
}

/**
 * Vec3 (x, y, z) değerlerini buffer'a yazar (24 bytes)
 */
function writeVec3(buffer, offset, point) {
  writeDoubleLE(buffer, offset, point.x || 0);
  writeDoubleLE(buffer, offset + 8, point.y || 0);
  writeDoubleLE(buffer, offset + 16, point.z || 0);
}

/**
 * Position header oluşturur (98 bytes)
 * @param {Object} bounds - Köşe koordinatları
 * @param {number} imageWidth - Resim genişliği (piksel)
 * @param {number} imageHeight - Resim yüksekliği (piksel)
 */
function createPositionHeader(bounds, imageWidth, imageHeight) {
  const header = new Uint8Array(POSITION_HEADER_SIZE);
  const view = new DataView(header.buffer);

  // Mystery value - AutoCAD örneğinde 0x5580
  // Bu değer resim boyutuyla ilgili olabilir, sabit kullanıyoruz
  view.setUint16(0, 0x5580, true);

  // Corner positions (her biri 24 bytes = 3 doubles)
  writeVec3(header, 2, bounds.upperLeft);
  writeVec3(header, 26, bounds.upperRight);
  writeVec3(header, 50, bounds.lowerRight);
  writeVec3(header, 74, bounds.lowerLeft);

  return header;
}

/**
 * OLE metadata header oluşturur (30 bytes)
 * Bu kısım CFB'den hemen önce gelir
 * @param {number} width - Image width in pixels
 * @param {number} height - Image height in pixels
 */
function createOLEMetadataHeader(width, height) {
  const header = new Uint8Array(OLE_METADATA_SIZE);
  const view = new DataView(header.buffer);

  let offset = 0;

  // Padding
  view.setUint16(offset, 0x0000, true); offset += 2;

  // Size related values - use image dimensions
  // AutoCAD örneğinde bunlar muhtemelen HIMETRIC veya benzeri birimde
  // Şimdilik piksel boyutlarını kullan
  view.setUint16(offset, width & 0xFFFF, true); offset += 2;
  view.setUint16(offset, height & 0xFFFF, true); offset += 2;

  // Flags - OLE type indicators
  view.setUint32(offset, 0x00000001, true); offset += 4;  // Version/type
  view.setUint32(offset, 0x00010000, true); offset += 4;  // Unknown flag
  view.setUint32(offset, 0x00000003, true); offset += 4;  // OLE type (3=static)

  // Reserved zeros
  view.setUint32(offset, 0x00000000, true); offset += 4;
  view.setUint32(offset, 0x00000000, true); offset += 4;

  // Additional flag
  view.setUint32(offset, 0x00000001, true); offset += 4;

  return header;
}

/**
 * Windows BMP dosyası oluşturur (32-bit BGRX format)
 * AutoCAD için uygun format - BITMAPFILEHEADER + BITMAPINFOHEADER + pixel data
 * NOT: BI_BITFIELDS yerine BI_RGB (0) kullanıyoruz çünkü daha yaygın destekleniyor
 */
function createBitmapData(imageData) {
  const { data, width, height } = imageData;

  // BMP dosya yapısı (32-bit BI_RGB):
  // - BITMAPFILEHEADER (14 bytes)
  // - BITMAPINFOHEADER (40 bytes)
  // - Pixel data (no color table for 32-bit)

  const headerSize = 14;  // BITMAPFILEHEADER
  const infoHeaderSize = 40;  // BITMAPINFOHEADER
  const pixelDataOffset = headerSize + infoHeaderSize;

  // Row stride (must be multiple of 4 bytes) - already satisfied for 32-bit
  const bytesPerPixel = 4;  // 32-bit
  const rowStride = width * bytesPerPixel;
  const pixelDataSize = rowStride * height;

  const totalSize = pixelDataOffset + pixelDataSize;

  const buffer = new Uint8Array(totalSize);
  const view = new DataView(buffer.buffer);

  let offset = 0;

  // ===== BITMAPFILEHEADER (14 bytes) =====
  buffer[offset] = 0x42; buffer[offset + 1] = 0x4D;  // "BM"
  offset += 2;
  view.setUint32(offset, totalSize, true); offset += 4;  // File size
  view.setUint16(offset, 0, true); offset += 2;  // Reserved1
  view.setUint16(offset, 0, true); offset += 2;  // Reserved2
  view.setUint32(offset, pixelDataOffset, true); offset += 4;  // Pixel data offset

  // ===== BITMAPINFOHEADER (40 bytes) =====
  view.setUint32(offset, infoHeaderSize, true); offset += 4;  // Header size
  view.setInt32(offset, width, true); offset += 4;  // Width
  view.setInt32(offset, height, true); offset += 4;  // Height (positive = bottom-up)
  view.setUint16(offset, 1, true); offset += 2;  // Planes
  view.setUint16(offset, 32, true); offset += 2;  // Bits per pixel
  view.setUint32(offset, 0, true); offset += 4;  // Compression (BI_RGB = 0)
  view.setUint32(offset, pixelDataSize, true); offset += 4;  // Image size
  view.setInt32(offset, 2835, true); offset += 4;  // X pels per meter (~72 DPI)
  view.setInt32(offset, 2835, true); offset += 4;  // Y pels per meter (~72 DPI)
  view.setUint32(offset, 0, true); offset += 4;  // Colors used
  view.setUint32(offset, 0, true); offset += 4;  // Important colors

  // ===== Pixel data (BGRX format, bottom-up) =====
  // Bottom-up means we write rows from bottom to top
  for (let y = 0; y < height; y++) {
    const srcY = height - 1 - y;  // Flip vertically for bottom-up
    for (let x = 0; x < width; x++) {
      const srcIdx = (srcY * width + x) * 4;
      const dstIdx = pixelDataOffset + (y * rowStride) + (x * 4);

      // RGBA -> BGRX (X = padding/reserved byte, often 0xFF)
      buffer[dstIdx] = data[srcIdx + 2];      // B
      buffer[dstIdx + 1] = data[srcIdx + 1];  // G
      buffer[dstIdx + 2] = data[srcIdx];      // R
      buffer[dstIdx + 3] = 0xFF;              // X (reserved)
    }
  }

  return buffer;
}

/**
 * Ole stream oluşturur (20 bytes)
 */
function createOleStream() {
  const buffer = new Uint8Array(20);
  const view = new DataView(buffer.buffer);

  view.setUint32(0, 0x00000001, true);   // Version
  view.setUint32(4, 0x00000002, true);   // Flags (embedded)
  // Rest is zeros (reserved)

  return buffer;
}

/**
 * CompObj stream oluşturur - AutoCAD formatına uygun
 */
function createCompObjStream() {
  // AutoCAD'den gelen değerler:
  // "Picture (Device Independent Bitmap)"
  // "StaticDib"

  const userType = 'Picture (Device Independent Bitmap)';
  const clipFormat = 'StaticDib';

  // CLSID for Static DIB: {00000316-0000-0000-C000-000000000046}
  const clsid = new Uint8Array([
    0x16, 0x03, 0x00, 0x00,
    0x00, 0x00,
    0x00, 0x00,
    0xC0, 0x00,
    0x00, 0x00, 0x00, 0x00, 0x00, 0x46
  ]);

  const userTypeBytes = new TextEncoder().encode(userType + '\0');
  const clipFormatBytes = new TextEncoder().encode(clipFormat + '\0');

  // CompObj structure:
  // - Reserved1: 4 bytes
  // - Version: 4 bytes
  // - Reserved2: 4 bytes
  // - Reserved3: 4 bytes
  // - CLSID: 16 bytes
  // Total fixed = 32 bytes
  const fixedSize = 32;
  const totalSize = fixedSize + 4 + userTypeBytes.length + 4 + clipFormatBytes.length;

  const buffer = new Uint8Array(totalSize);
  const view = new DataView(buffer.buffer);

  let offset = 0;

  // Reserved1 (must be 0x00000001)
  view.setUint32(offset, 0x00000001, true); offset += 4;

  // Version (must be 0xFFFFFFFF for version 2)
  view.setUint32(offset, 0xFFFFFFFF, true); offset += 4;

  // Reserved2
  view.setUint32(offset, 0x0000000A, true); offset += 4;
  view.setUint32(offset, 0x00000003, true); offset += 4;

  // CLSID
  buffer.set(clsid, offset); offset += 16;

  // UserType length + string
  view.setUint32(offset, userTypeBytes.length, true); offset += 4;
  buffer.set(userTypeBytes, offset); offset += userTypeBytes.length;

  // ClipFormat length + string
  view.setUint32(offset, clipFormatBytes.length, true); offset += 4;
  buffer.set(clipFormatBytes, offset);

  return buffer;
}

/**
 * CFB (Compound File Binary) container oluşturur
 */
function createCFBContainer(bmpData) {
  const cfb = CFB.utils.cfb_new();

  // Ole stream
  const oleStream = createOleStream();
  CFB.utils.cfb_add(cfb, '/\x01Ole', oleStream);

  // CONTENTS stream (BMP data)
  CFB.utils.cfb_add(cfb, '/CONTENTS', bmpData);

  // CompObj stream
  const compObjStream = createCompObjStream();
  CFB.utils.cfb_add(cfb, '/\x01CompObj', compObjStream);

  // Write CFB to buffer
  const cfbBuffer = CFB.write(cfb, { type: 'array' });

  return new Uint8Array(cfbBuffer);
}

/**
 * Binary blob'u DXF 310 group code formatına dönüştürür
 */
function blobToHexLines(blob) {
  const lines = [];
  const CHUNK_SIZE = 127;  // Max bytes per 310 line

  for (let i = 0; i < blob.length; i += CHUNK_SIZE) {
    const chunk = blob.slice(i, Math.min(i + CHUNK_SIZE, blob.length));
    const hex = Array.from(chunk)
      .map(b => b.toString(16).padStart(2, '0').toUpperCase())
      .join('');
    lines.push(hex);
  }

  return lines;
}

/**
 * ImageData'dan OLE2FRAME blob oluşturur
 * @param {Object} imageData - {data, width, height}
 * @param {Object} bounds - DXF koordinatları
 * @returns {Object} {hexLines, totalSize}
 */
export function createOLE2FrameBlob(imageData, bounds) {
  const { width, height } = imageData;

  // 1. Position header (98 bytes)
  const positionHeader = createPositionHeader(bounds, width, height);

  // 2. Create BMP data
  const bmpData = createBitmapData(imageData);

  // 3. OLE metadata header (30 bytes) - pass image dimensions
  const oleMetadata = createOLEMetadataHeader(width, height);

  // 4. Create CFB container
  const cfbContainer = createCFBContainer(bmpData);

  // 5. Combine all parts
  const totalSize = positionHeader.length + oleMetadata.length + cfbContainer.length;
  const fullBlob = new Uint8Array(totalSize);

  let offset = 0;
  fullBlob.set(positionHeader, offset); offset += positionHeader.length;
  fullBlob.set(oleMetadata, offset); offset += oleMetadata.length;
  fullBlob.set(cfbContainer, offset);

  // 6. Convert to hex lines
  const hexLines = blobToHexLines(fullBlob);

  return {
    hexLines,
    totalSize,
    bmpSize: bmpData.length,
    cfbSize: cfbContainer.length
  };
}

/**
 * Pozisyon ve boyuttan bounds hesaplar
 */
export function calculateBounds(position, width, height) {
  const x = position.x || 0;
  const y = position.y || 0;
  const z = position.z || 0;

  return {
    upperLeft: { x, y: y + height, z },
    upperRight: { x: x + width, y: y + height, z },
    lowerRight: { x: x + width, y, z },
    lowerLeft: { x, y, z }
  };
}

/**
 * DXF OLE2FRAME entity string oluşturur
 * AutoCAD DXF Reference: https://help.autodesk.com/cloudhelp/2020/ENU/AutoCAD-DXF/files/GUID-77747CE6-82C6-4452-97ED-4CEEB38BE960.htm
 */
export function generateOLE2FrameDXF(options) {
  const { hexLines, bounds, layerName = '0', handle = 'FFFF' } = options;

  const binaryDataSize = hexLines.reduce((sum, line) => sum + line.length / 2, 0);

  // DXF format: group code + newline + value + newline
  // No leading/trailing spaces - simple format
  const lines = [
    '0',
    'OLE2FRAME',
    '5',
    handle,
    '100',
    'AcDbEntity',
    '8',
    layerName,
    '100',
    'AcDbOle2Frame',
    '70',
    '2',
    '3',
    'OLE',
    '10',
    bounds.upperLeft.x.toFixed(6),
    '20',
    bounds.upperLeft.y.toFixed(6),
    '30',
    (bounds.upperLeft.z || 0).toFixed(6),
    '11',
    bounds.lowerRight.x.toFixed(6),
    '21',
    bounds.lowerRight.y.toFixed(6),
    '31',
    (bounds.lowerRight.z || 0).toFixed(6),
    '71',
    '1',
    '72',
    '0',
    '90',
    binaryDataSize.toString()
  ];

  // Add binary data lines (group code 310)
  for (const hexLine of hexLines) {
    lines.push('310', hexLine);
  }

  // Add OLE link name (group code 1) - required field
  lines.push('1', 'OLE');

  return lines.join('\n');
}

/**
 * URL'den resim yükler
 */
export async function loadImageFromURL(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      resolve({
        imageData,
        width: canvas.width,
        height: canvas.height
      });
    };

    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
}

export default {
  createOLE2FrameBlob,
  calculateBounds,
  generateOLE2FrameDXF,
  loadImageFromURL,
  blobToHexLines
};
